import { z } from "zod";

// Template library: admin-built documents (service agreements, estimate
// terms, waivers…) grouped by free-form category — the category is where reps
// find them. Bodies carry merge fields like {{customer.name}}, {{job.title}},
// {{company.name}}, {{date}} that render when an agreement is created.
export const DocTemplateSchema = z.object({
  name: z.string().min(1).max(120),
  category: z.string().min(1).max(60),
  body: z.string().min(1).max(20000),
  requiresSignature: z.boolean().default(true),
  /** Who signs: the customer (most documents) or the company (lien waivers and notices). */
  signer: z.enum(["customer", "company"]).optional(),
  archivedAt: z.number().nullable().default(null),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type DocTemplate = z.infer<typeof DocTemplateSchema>;

export const AgreementStatusSchema = z.enum(["pending", "signed", "void"]);
export type AgreementStatus = z.infer<typeof AgreementStatusSchema>;

// A rendered template instance attached to a job/customer. The body is a
// SNAPSHOT — later template edits never rewrite what someone signed. Signing
// happens on the rep's device (present mode) or through the emailed
// /sign/... link; signToken is the link's secret.
export const AgreementSchema = z.object({
  templateId: z.string(),
  templateName: z.string().default(""),
  category: z.string().default(""),
  customerId: z.string().nullable().default(null),
  customerName: z.string().default(""),
  jobId: z.string().nullable().default(null),
  body: z.string().max(30000),
  status: AgreementStatusSchema.default("pending"),
  requiresSignature: z.boolean().default(true),
  signerName: z.string().nullable().default(null),
  signaturePngBase64: z.string().max(400000).nullable().default(null),
  signedAt: z.number().nullable().default(null),
  signToken: z.string().nullable().default(null),
  sentToEmail: z.string().default(""),
  /** Who signs: the customer, or someone for the company ("By" / "Its" on lien documents). */
  signer: z.enum(["customer", "company"]).optional(),
  /** The company signer's title ("Its": Owner, Office manager…). */
  signerTitle: z.string().max(120).nullable().optional(),
  /** Nevada lien documents: which one, and what it's for. */
  lienDoc: z.enum(["conditional_progress", "unconditional_progress", "conditional_final", "unconditional_final", "notice"]).optional(),
  invoiceId: z.string().optional(),
  projectId: z.string().optional(),
  lotId: z.string().nullable().optional(),
  /** A notice of right to lien: when and how it went out (certified mail number…). */
  sentAt: z.number().nullable().optional(),
  sentHow: z.string().max(200).optional(),
  createdByUid: z.string(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Agreement = z.infer<typeof AgreementSchema>;
